import { collection, addDoc, query, where, getDocs, serverTimestamp, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { sendWebDesignConfirmationEmail } from './emailService';


const WD_COLLECTION = 'webDesignRegistrations';

export const submitWebDesignRegistration = async (formData) => {
  // Basic duplicate/limit checks can be expanded as needed
  // Check duplicates by email/phone/enrollment
  const normalizedEmail = (formData.email || '').toLowerCase();
  const normalizedPhone = (formData.contactNumber || '').replace(/\D/g, '');
  const normalizedEnrollment = (formData.enrollmentNo || '').toUpperCase();

  // Email
  if (normalizedEmail) {
    const qEmail = query(collection(db, WD_COLLECTION), where('email', '==', normalizedEmail));
    const emailSnap = await getDocs(qEmail);
    if (!emailSnap.empty) {
      throw new Error('This email has already been registered for the Web Design Competition.');
    }
  }
  // Phone
  if (normalizedPhone) {
    const allDocs = await getDocs(collection(db, WD_COLLECTION));
    const phoneDup = allDocs.docs.some(d => (d.data().contactNumber || '').replace(/\D/g, '') === normalizedPhone);
    if (phoneDup) {
      throw new Error('This phone number has already been registered for the Web Design Competition.');
    }
  }
  // Enrollment
  if (normalizedEnrollment) {
    const allDocs = await getDocs(collection(db, WD_COLLECTION));
    const enrDup = allDocs.docs.some(d => (d.data().enrollmentNo || '').toUpperCase() === normalizedEnrollment);
    if (enrDup) {
      throw new Error('This enrollment number has already been registered for the Web Design Competition.');
    }
  }

  const payload = {
    ...formData,
    email: normalizedEmail,
    submittedAt: serverTimestamp(),
    type: 'web-design',
    selectedDay: formData.selectedDay || 'Web Design Competition'
  };

  await addDoc(collection(db, WD_COLLECTION), payload);

  // Send confirmation email
  try {
    await sendWebDesignConfirmationEmail(formData);
    console.log('✅ Web Design Competition confirmation email sent to:', formData.email);
  } catch (emailError) {
    console.error('❌ Failed to send Web Design Competition confirmation email:', emailError);
    // Don't throw error - registration succeeded even if email fails
  }
};

export const getWebDesignRegistrationCount = async () => {
  const snap = await getDocs(collection(db, WD_COLLECTION));
  return snap.size;
};

export const getAllWebDesignRegistrations = async () => {
  const snap = await getDocs(collection(db, WD_COLLECTION));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteWebDesignRegistration = async (id) => {
  await deleteDoc(doc(db, WD_COLLECTION, id));
};

export const checkEmailExists = async (email) => {
  const q = query(collection(db, WD_COLLECTION), where('email', '==', (email || '').toLowerCase()));
  const s = await getDocs(q);
  return !s.empty;
};

export const checkPhoneNumberExists = async (phone) => {
  const p = (phone || '').replace(/\D/g, '');
  const s = await getDocs(collection(db, WD_COLLECTION));
  return s.docs.some(d => (d.data().contactNumber || '').replace(/\D/g, '') === p);
};

export const checkEnrollmentNumberExists = async (enrollment) => {
  const e = (enrollment || '').toUpperCase();
  const s = await getDocs(collection(db, WD_COLLECTION));
  return s.docs.some(d => (d.data().enrollmentNo || '').toUpperCase() === e);
};

/**
 * Update attended status for a web design registration
 * @param {string} registrationId - The registration document ID
 * @param {boolean} attended - The attended status
 * @returns {Promise<void>}
 */
export const updateAttendedStatus = async (registrationId, attended) => {
  try {
    const docRef = doc(db, WD_COLLECTION, registrationId);
    await updateDoc(docRef, {
      attended: attended,
      attendedUpdatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating attended status:', error);
    throw new Error('Failed to update attended status');
  }
};

/**
 * Delete ALL Web Design registrations
 * @returns {Promise<number>} Number of deleted registrations
 */
export const deleteAllWebDesignRegistrations = async () => {
  try {
    console.log('Starting delete all web design registrations...');
    const snapshot = await getDocs(collection(db, WD_COLLECTION));

    if (snapshot.size === 0) return 0;

    let deletedCount = 0;
    for (const doc of snapshot.docs) {
      await deleteDoc(doc.ref);
      deletedCount++;
    }

    return deletedCount;
  } catch (error) {
    console.error('Error deleting all web design registrations:', error);
    throw new Error('Failed to delete all web design registrations');
  }
};
